// Standard library imports
import React, { useEffect, useState } from 'react';
// External library imports
import { get as lodashGet, omit as lodashOmit } from 'lodash';
import { toast } from 'react-toastify';
import { Form, Formik } from 'formik';

import { useLoader, useUserRole } from '../../../../hooks';
import Checkbox from '../../../../components/Inputs/Checkbox/Checkbox';
import Typography from '../../../../components/Typography/Typography';
import allDeviceClasses from '../../../../styles/AllDevices.module.css';
import bidClasses from '../Bidding.module.css';
import Dropdown from '../../../../components/Inputs/Dropdown';
import { Input } from '../../../../components/Inputs/Input';
import { momentTimeFormater } from '../../../../utils/timeHelper';
import { getColumnWithSlotSize } from '../../../../utils/biddingHelper';
import { RootBiddingService } from '../../../../services/BiddingService/Root/BiddingService';
import { AdminBiddingService } from '../../../../services/BiddingService/Admin/BiddingService';
import { updatePostedEnergyBidRegulationPrice } from '../../../../validations/BiddingService/Bidding';
import { RootBundleBiddingService } from '../../../../services/BiddingService/Root/BundleBiddingService';
import { AdminBundleBiddingService } from '../../../../services/BiddingService/Admin/BundleBiddingService';

const STATUS_OPTION = [
    {
        label: 'Inactive',
        value: 'inactive',
    },
    {
        label: 'Active',
        value: 'active',
    },
];

const ENERGY_BID_TYPE = [
    {
        label: 'Direct',
        value: 'direct',
    },
    {
        label: 'Schedule',
        value: 'schedule',
    },
];

export const UpdatePostedEnergyBid = ({ bid, closeModal, bidType }) => {
    const { isRootUser } = useUserRole();
    const service = isRootUser ? (bidType === 'bundle' ? RootBundleBiddingService : RootBiddingService) : bidType === 'bundle' ? AdminBundleBiddingService : AdminBiddingService;
    const length = lodashGet(bid, ['primary', 'regulationPrice'], []).length;
    const slots = Array.from({ length: length }, (__, i) => i);
    const columns = getColumnWithSlotSize(slots, 8);
    const [startLoader, stopLoader] = useLoader();
    const [selectedHours, setSelectedHours] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState(STATUS_OPTION[0]);
    const regulationPrice = lodashGet(bid, ['primary', 'regulationPrice'], []);
    const [initialValues, setInitialValues] = useState({
        status: STATUS_OPTION[0],
        regulationPrice: {},
    });
    const [selectedEnergyBidType, setSelectedEnergyBidType] = useState(ENERGY_BID_TYPE[0]);
    const currentTime = { aFRR: momentTimeFormater().add(25, 'minute'), mFRR: momentTimeFormater().add(45, 'minute') };
    const marketType = bid?.market?.includes('aFRR') ? 'aFRR' : 'mFRR';

    useEffect(() => {
        if (marketType === 'mFRR' && bid?._id) getEnergyBidType();
    }, []);

    const getCheckedhours = (value, hour) => {
        if (value) setSelectedHours([...selectedHours, hour]);
        else setSelectedHours(selectedHours.filter((e) => e !== hour));
    };

    const handleSubmit = (values) => {
        const payload = { selectedHours, ...values, bidId: bid._id, energyBidType: values?.energyBidType };
        if (selectedHours.length) service.UpdatePostedEnergyBid(payload, startLoader, handleSubmitSuccess, handleError, stopLoader);
        else toast.error('Please select hours');
    };

    const handleSubmitSuccess = () => {
        toast.success('Energi Bid posted successfully');
        closeModal();
    };

    const handleError = (err) => toast.error(lodashGet(err, ['response', 'data', 'message']));

    const getEnergyBidType = () => {
        const params = { date: bid.date };
        service.GetEnergyBidType(bid._id, params, startLoader, handleSuccess, handleError, stopLoader);
    };

    const handleSuccess = ({ data }) => {
        if (lodashGet(data, 'data') === 'schedule') setSelectedEnergyBidType(ENERGY_BID_TYPE[1]);
        else setSelectedEnergyBidType(ENERGY_BID_TYPE[0]);
    };

    return (
        <div className={`${bidClasses.InfoModalContainer}`}>
            <div>
                <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={updatePostedEnergyBidRegulationPrice(selectedHours, selectedStatus.value)}>
                    {({ errors, values, ...props }) => (
                        <Form>
                            <div className={bidClasses.Flex} style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography content={'Availability hours'} size={16} />
                                <div className={bidClasses.Flex}>
                                    <div className={allDeviceClasses.FieldControl}>
                                        <label>
                                            Status <span className="required">*</span>
                                        </label>
                                        <div>
                                            <Dropdown name="status" options={STATUS_OPTION} defaultValue={STATUS_OPTION[0]} onChange={setSelectedStatus} />
                                        </div>
                                    </div>
                                    {marketType === 'mFRR' && (
                                        <div className={allDeviceClasses.FieldControl}>
                                            <label>
                                                Energy Bid Type <span className="required">*</span>
                                            </label>
                                            <div>
                                                <Dropdown name={'energyBidType'} options={ENERGY_BID_TYPE} defaultValue={selectedEnergyBidType} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className={bidClasses.TableContainer}>
                                {columns.map((column, colIndex) => (
                                    <table key={colIndex}>
                                        <tbody>
                                            {column.map((key) => (
                                                <tr>
                                                    <td>
                                                        <Checkbox
                                                            checked={selectedHours.includes(key) ? true : false}
                                                            onChange={(e) => {
                                                                getCheckedhours(e, key, values.status);
                                                                if (values.status === 'active') {
                                                                    if (e) props.setFieldValue(`regulationPrice.${key}`, lodashGet(regulationPrice, [key], 0));
                                                                    else {
                                                                        const updatedObj = lodashOmit(values?.regulationPrice, key);
                                                                        props.setFieldValue(`regulationPrice`, updatedObj);
                                                                    }
                                                                }
                                                            }}
                                                            disabled={lodashGet(currentTime, [marketType]).isAfter(momentTimeFormater(bid.date).add(key, 'hour'))}
                                                        />
                                                    </td>

                                                    {values.status === 'active' && selectedHours.includes(key) ? (
                                                        <td>
                                                            <div className={allDeviceClasses.FieldControl}>
                                                                <label>
                                                                    HOUR__{momentTimeFormater(bid.date).add(key, 'hour').hour()} Regulation Price
                                                                    <span className="required">*</span>
                                                                </label>
                                                                <Input name={`regulationPrice.${key}`} type={'number'} id="regulationPrice" style={{ marginTop: '0' }} />
                                                            </div>
                                                        </td>
                                                    ) : (
                                                        <td>HOUR__{momentTimeFormater(bid.date).add(key, 'hour').hour()}</td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ))}
                            </div>

                            <div className={allDeviceClasses.ButtonContainer}>
                                <div>
                                    <button type="button" className="btn-secondary" onClick={closeModal}>
                                        Cancel
                                    </button>
                                </div>
                                <div>
                                    <button type="submit" className="btn-primary">
                                        Submit
                                    </button>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};
